import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";

// Separate component to handle the background color effect
const BackgroundEffect = ({ color }) => {
  useEffect(() => {
    if (typeof window === "undefined" || !document) return;

    // Only apply changes if we have a color (avoid null resets)
    if (color) {
      document.body.style.transition = "background-color 0.3s ease";
      document.body.style.backgroundColor = color;
    }
  }, [color]);

  return null; // This component doesn't render anything
};

const BackgroundContext = createContext();
const TextStyleContext = createContext();

// Helper function to convert hex to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

// Helper function to calculate relative luminance
const getLuminance = (r, g, b) => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

// Helper function to calculate contrast ratio
const getContrastRatio = (color1, color2) => {
  const lum1 = getLuminance(color1.r, color1.g, color1.b);
  const lum2 = getLuminance(color2.r, color2.g, color2.b);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
};

export const BackgroundProvider = ({ children }) => {
  const [colorHex, setColorHex] = useState(null);
  const [whiteText, setWhiteText] = useState(false);

  const setBackgroundColor = useCallback((color) => {
    // Validate the color format
    if (
      color === null ||
      typeof color !== "string" ||
      !/^([0-9A-F]{3}|[0-9A-F]{6})$/i.test(color)
    ) {
      setColorHex("#ffffff");
      setWhiteText(false);
      return;
    }

    const fullHex = "#" + color;
    setColorHex(fullHex);

    // Calculate contrast ratio between black text (#000) and background
    const blackText = { r: 0, g: 0, b: 0 };
    const backgroundColor = hexToRgb(fullHex);

    if (backgroundColor) {
      const contrastRatio = getContrastRatio(blackText, backgroundColor);
      // WCAG AA standard requires 4.5:1 for normal text, 3:1 for large text
      // Using 4.5 as threshold for better accessibility
      setWhiteText(contrastRatio < 4.5);
    }
  }, []);

  const backgroundValue = useMemo(
    () => ({
      setBackgroundColor,
    }),
    [setBackgroundColor]
  );

  const textStyleValue = useMemo(
    () => ({
      whiteText,
      setWhiteText,
    }),
    [whiteText]
  );

  return (
    <BackgroundContext.Provider value={backgroundValue}>
      <TextStyleContext.Provider value={textStyleValue}>
        <BackgroundEffect color={colorHex} />
        {children}
      </TextStyleContext.Provider>
    </BackgroundContext.Provider>
  );
};

export const useBackground = () => {
  const context = useContext(BackgroundContext);
  // Return default values if context is undefined (during SSR)
  return context || { setBackgroundColor: () => {} };
};

export const useTextStyle = () => {
  const context = useContext(TextStyleContext);
  // Return default values if context is undefined (during SSR)
  return context || { whiteText: false, setWhiteText: () => {} };
};
